import Layout from "@/components/Layout";
import bannerAsset from "@/assets/banneri-osaaminen.jpg";
import { PageDecorations } from "@/components/Decorations";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageContent } from "@/lib/usePageContent";
import { ExternalLink } from "@/components/ExternalLink";

const FALLBACK = { hero_badge: "CV", hero_title: "Osaaminen" };

const STRENGTHS: Array<{ title: string; body: string; bg: string }> = [
  {
    title: "Sivistys ja nuorten tulevaisuus",
    body: "Kasvatustieteellinen koulutukseni ja pitkä kokemukseni nuoriso- ja opiskelijatoiminnasta muodostavat vahvan perustan koulutuspoliittiselle osaamiselleni. Olen perehtynyt koulutusjärjestelmän kehittämiseen, osallisuuden vahvistamiseen ja nuorten hyvinvointiin sekä teoriassa että käytännössä. Näen koulutuksen ja sivistyksen keskeisinä hyvinvoivan ja kilpailukykyisen yhteiskunnan rakentajina.",
    bg: "hsl(38 45% 93%)",
  },
  {
    title: "Järjestöjohtaminen ja yhteiskunnallinen vaikuttaminen",
    body: "Olen toiminut useissa luottamus- ja johtotehtävissä paikallisella, alueellisella ja valtakunnallisella tasolla. Kokemukseeni kuuluu hallitustyö, puheenjohtajuudet, talousvastuu sekä strateginen kehittäminen kansalaisjärjestöissä. Järjestötoiminta on opettanut minulle pitkäjänteistä vaikuttamista, erilaisten ihmisten johtamista ja yhteisten tavoitteiden saavuttamista vapaaehtoistoiminnan voimin. Lisäksi minulla on kokemusta poliittisesta päätöksenteosta ja sitä tukevasta valmistelutyöstä niin eduskunnassa ja puolueorganisaatiossa kuin kunta- ja hyvinvointialuetasolla. Tunnen päätöksenteon arjen ja tiedän, miten vaikuttava politiikka syntyy yhteistyössä.",
    bg: "hsl(206 42% 90%)",
  },
  {
    title: "Ihmiseltä ihmiselle",
    body: "Minulla on laaja kokemus työstä, jossa onnistuminen perustuu ihmisten kohtaamiseen, luottamuksen rakentamiseen ja selkeään viestintään. Olen tehnyt työtä viestinnän, verkostomarkkinoinnin, myynnin sekä ravintola- ja matkailualan asiakaspalvelutehtävissä, joissa kyky kuunnella, reagoida ja toimia paineessa on keskeistä. Tämä on opettanut minulle ihmisten kohtaamista, kuuntelemista ja ratkaisukeskeistä toimintaa.",
    bg: "hsl(340 28% 91%)",
  },
];

const OPINNOT = [
  "Kasvatustieteen maisteri, yleinen kasvatustiede, arv. 06/2026",
  "Johtamisen ja yritysjohtamisen erikoisammattitutkinto, 06/2025",
  "Yhteiskuntatieteiden ylioppilas, matkailututkimus",
  "Kasvatustieteen kandidaatti, 03/2024",
  "Value Based Leadership, 05/2021",
  "Partiojohtajan peruskoulutus, 08/2017",
];

const TYO = [
  "Kansanedustajan avustaja, Kokoomuksen eduskuntaryhmä 02/2025 alkaen",
  "Itsenäinen jälleenmyyjä, Forever Living Products 06/2016 alkaen",
  "Tarjoilija, Kärppien kotipelit kaudelta 2022–2023 alkaen",
  "Nuorisokoordinaattori 10/2023–5/2025 sekä Järjestösihteeri 12/2023–02/2026, Pohjois-Pohjanmaan Kokoomus",
  "Viestinnän harjoittelija, Oulun kaupunki / Kiertokaari 07/2024",
  "Tarjoilija, kesäravintola Mustakari 05/2023–08/2023",
  "Kioskimyyjä, R-kioski 05/2019–08/2023",
  "Ohjaaja, Talous ja Nuoret TAT yrityskylä 09/2022–05/2023",
  "Vaalikoordinaattori, Pohjois-Pohjanmaan Kokoomus 11/2022–04/2023",
  "Vastaanottotyöntekijä, Hotelli Kokkola 05/2022–08/2022",
  "Harrastetyöntekijä, Holiday Club Saimaa 06/2021–08/2021",
  "Kassatyöntekijä, K-market Kilpishalli 06/2018–07/2018",
  "Myyntineuvottelija, Suomen Vitamiinikeskus 06/2017–07/2017",
  "Nuorisotyön apuohjaaja / Kausityöntekijä / Kerhonohjaaja / Isonen, Taipalsaaren seurakunta 08/2013–08/2017",
  "Kotiseututalon opas, Taipalsaaren kunta 07/2015",
];

const LUOTTAMUS: Array<{ org: string; items: string[] }> = [
  {
    org: "Oulun kaupunki",
    items: [
      "OSAOn yhtymäkokousedustaja 08/2021 alkaen",
      "2022–2025 sivistyslautakunnan varajäsen",
    ],
  },
  {
    org: "Pohde",
    items: [
      "2025–2029 Turvallisuus- ja valmiuslautakunnan varajäsen",
      "2022–2025 Oulun alueellisen neuvottelukunnan varajäsen, yhdyspintalautakunnan varajäsen",
    ],
  },
  {
    org: "Kokoomusyhteisö",
    items: [
      "Pohjois-Pohjanmaan Kokoomuksen varapuheenjohtaja 2026",
      "Kokoomuksen Nuorten Liiton hallituksen jäsen 2025",
      "Oulun Läänin Kokoomusnuorten varapuheenjohtaja 2024",
      "Oulun Opiskelevien Kokoomusnuorten puheenjohtaja 2023",
    ],
  },
];

const AIEMMAT_LUOTTAMUS: Array<{ org: string; items: string[] }> = [
  {
    org: "Oulun yliopiston ylioppilaskunta",
    items: [
      "Talousvaliokunnan puheenjohtaja 2023–2024",
      "Hallituksen jäsen 2022",
      "Edustajiston jäsen/varajäsen 11/2019 alkaen",
    ],
  },
  {
    org: "Oulun yliopisto",
    items: [
      "Yliopistokollegion jäsen",
      "Koulutuksen johtoryhmän jäsen",
      "Tiedekunnan johtoryhmän jäsen",
      "YUNET-verkoston johtoryhmän jäsen",
      "Tiedekuntahallituksen jäsen",
      "Tiedekunnan koulutustoimikunnan jäsen ja varajäsen",
    ],
  },
  {
    org: "Kansallinen kulttuuriliitto",
    items: [
      "Liittohallituksen jäsen 2024–2026",
      "Paikallisjärjestön puheenjohtaja 2025 alkaen",
    ],
  },
  { org: "SYL", items: ["Kehitysyhteistyöneuvottelukunnan jäsen 2022–2024"] },
  {
    org: "Taipalsaaren Taivaltajat ry",
    items: [
      "Lippukunnanjohtaja 2020–2022",
      "Lippukunnan apulaisjohtaja 2019",
      "Lippukunnan hallituksen jäsen 2015–2017",
      "Joulukampanjapäällikkö 2016",
    ],
  },
  {
    org: "Suomen Kasvatustieteiden Opiskelijoiden Liitto",
    items: ["EPALE-lähettiläs 2020–2022"],
  },
  { org: "Suomen Partiolaiset", items: ["Vaalitoimikunnan puheenjohtaja 2022"] },
  { org: "Lapin Partiolaiset", items: ["Partiotahdin viestintävastaava 2021"] },
  {
    org: "Oulu Entrepreneurship Society",
    items: ["Sihteeri ja yhteisövastaava 2020"],
  },
  {
    org: "Etelä-Karjalan Partiolaiset",
    items: [
      "Piirileirin ikäkauden ohjelmapäällikkö 2019–2021",
      "Jäsenlehden päätoimittaja 2017–2019",
    ],
  },
  {
    org: "Oulun Kasvatustieteiden kilta, Kaski ry",
    items: ["Hallituksen jäsen 2019"],
  },
  { org: "Motiva ry", items: ["Hallituksen jäsen 2017–2019"] },
];

const BOLD_ITEMS = new Set<string>([
  "Kasvatustieteen maisteri, yleinen kasvatustiede, arv. 06/2026",
  "Johtamisen ja yritysjohtamisen erikoisammattitutkinto, 06/2025",
  "Kansanedustajan avustaja, Kokoomuksen eduskuntaryhmä 02/2025 alkaen",
  "Itsenäinen jälleenmyyjä, Forever Living Products 06/2016 alkaen",
]);

const ANSIOT = [
  { org: "Kansallinen Kokoomus rp", award: "Kokoomuksen Leijonamerkki: Vihreä leijona, 2024" },
  { org: "Kokoomuksen Nuorten liitto ry", award: "Harrastemerkki, 2024" },
  {
    org: "Pohjois-Pohjanmaan Kokoomus ry",
    award: "Keskuskauppakamarin Vuoden työntekijä -ansiomerkki, 2023",
  },
  { org: "Oulun yliopiston ylioppilaskunta", award: "Toisen luokan ansiomerkki, 2023" },
  { org: "Etelä-Karjalan Partiolaiset ry", award: "Pronssinen ansiomerkki, 2020" },
];

const Experience = () => {
  const { t } = usePageContent("experience", FALLBACK);
  return (
    <Layout>
      <section className="relative w-full overflow-hidden lg:h-[342px]">
        <img
          src={bannerAsset}
          alt="Lotta portaikossa"
          title="Kuvaaja: Koho, Eveliina"
          className="block h-auto w-full lg:h-full lg:object-cover lg:object-[center_25%]"
        />




        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-6 top-8 sm:left-10 sm:top-10 lg:left-14 lg:top-14">
            {t("hero_badge") && (
              <span className="inline-block rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-md md:text-sm">
                {t("hero_badge")}
              </span>
            )}
          </div>
          <div className="absolute left-0 top-[42%] w-full -translate-y-1/2 px-6 sm:w-3/5 sm:px-10 md:w-1/2 lg:w-[45%] lg:px-14">
            <h1 className="font-display text-4xl font-bold leading-tight text-white drop-shadow-[0_3px_10px_rgba(0,0,0,0.55)] sm:text-5xl md:text-6xl lg:text-7xl">
              {t("hero_title")}
            </h1>
          </div>
        </div>
      </section>



      <section
        className="relative overflow-hidden px-6 py-16 lg:px-10"
        style={{ background: "linear-gradient(180deg, hsl(39,82%,92%) 0%, hsl(42,86%,78%) 100%)" }}
      >
        <PageDecorations />
        <div className="relative mx-auto max-w-3xl space-y-8">
          {/* Intro */}
          <article className="rounded-[1.6rem] border border-border/60 bg-card/85 p-8 shadow-sm md:p-10">
            <p className="text-lg leading-relaxed text-foreground/85">
              Lotan vahvuuksia ovat poliittinen valmistelu ja vaikuttaminen, järjestö- ja
              luottamushenkilöjohtaminen sekä koulutus- ja nuorisopolitiikka. Vahva kokemus
              viestinnästä, sidosryhmäyhteistyöstä ja vaalikampanjoiden koordinoinnista sekä laaja
              hallinnollinen osaaminen julkisella ja kolmannella sektorilla tukevat hänen työtään
              poliittisena päättäjänä.
            </p>
          </article>

          {/* Vahvuudet (kolme värikästä palkkia) */}
          {STRENGTHS.map((s) => (
            <article
              key={s.title}
              className="rounded-[1.6rem] border border-border/60 p-8 shadow-sm md:p-10"
              style={{ background: s.bg }}
            >
              <h2 className="mb-5 font-display text-2xl font-bold italic leading-snug text-secondary md:text-3xl">
                {s.title}
              </h2>
              <p className="text-lg leading-relaxed text-foreground/85">{s.body}</p>
            </article>
          ))}

          {/* CV */}
          <div className="rounded-[1.8rem] border border-border bg-card/90 p-8 shadow-sm md:p-10">
            <h2 className="mb-6 font-display text-3xl font-bold italic text-foreground md:text-4xl">
              CV
            </h2>

            <Accordion type="multiple" className="space-y-1">
              {[
                { id: "opinnot", title: "Opinnot", flat: OPINNOT },
                { id: "tyo", title: "Työkokemus", flat: TYO },
                { id: "luottamus", title: "Luottamustoimet", grouped: LUOTTAMUS },
                { id: "aiemmat", title: "Muita organisaatioita, joissa aiempia luottamustoimia", grouped: AIEMMAT_LUOTTAMUS },
                {
                  id: "ansiot",
                  title: "Ansiot",
                  flat: ANSIOT.map((a) => `${a.org}: ${a.award}`),
                },
              ].map((sec) => (
                <AccordionItem key={sec.id} value={sec.id} className="border-b border-border/60 last:border-b-0">
                  <AccordionTrigger className="font-display text-xl font-semibold text-foreground">
                    {sec.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="relative ml-3 border-l-2 border-primary/60 pl-6 pt-2">
                      {sec.flat &&
                        sec.flat.map((item, i) => {
                          const colonIdx = sec.id === "ansiot" ? item.indexOf(": ") : -1;
                          const renderWithForeverLink = (s: string) => {
                            const key = "Forever Living Products";
                            const idx = s.indexOf(key);
                            if (idx === -1) return s;
                            return (
                              <>
                                {s.slice(0, idx)}
                                <ExternalLink href="https://460000742960.fbo.foreverliving.com/fin/fi-fi/home">
                                  {key}
                                </ExternalLink>
                                {s.slice(idx + key.length)}
                              </>
                            );
                          };
                          return (
                            <div key={i} className="relative mb-5 last:mb-0">
                              <span className="absolute -left-[31px] top-2 h-3 w-3 rounded-full border-2 border-primary bg-card" />
                              <p className={BOLD_ITEMS.has(item) ? "font-semibold text-foreground" : "text-foreground/85"}>
                                {colonIdx > -1 ? (
                                  <>
                                    <span className="font-semibold text-foreground">{item.slice(0, colonIdx + 1)}</span>
                                    {renderWithForeverLink(item.slice(colonIdx + 1))}
                                  </>
                                ) : (
                                  renderWithForeverLink(item)
                                )}
                              </p>
                            </div>
                          );
                        })}
                      {sec.grouped &&
                        sec.grouped.map((g) => (
                          <div key={g.org} className="relative mb-7 last:mb-0">
                            <h4 className="mb-2 font-semibold text-foreground">{g.org}</h4>
                            <ul className="list-disc space-y-1 pl-5 text-foreground/85 marker:text-foreground">
                              {g.items.map((i) => (
                                <li key={i}>{i}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Experience;
